import { FC, ChangeEvent, useCallback, memo, useMemo } from 'react';
import Switch from '@mui/material/Switch';
import { SxProps, Theme } from '@mui/material';

type SizeType = 'large' | 'medium' | 'small';

interface ControlledSwitchProps {
	id?: string;
	disabled?: boolean;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	checked: boolean;
	size?: SizeType;
	sx?: SxProps<Theme>;
}

const SIZE_SCALE_MAP: Record<SizeType, number> = {
	large: 1.5,
	medium: 1.3,
	small: 1
};

const ControlledSwitch: FC<ControlledSwitchProps> = memo(
	({ id, onChange, checked, disabled = false, size = 'medium' }) => {
		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				onChange?.(event);
			},
			[onChange]
		);

		const sxStyles: SxProps<Theme> = useMemo(
			() => ({
				transform: `scale(${SIZE_SCALE_MAP[size]})`,
				'& .MuiSwitch-switchBase.Mui-checked': {
					color: 'var(--euna-brand-primary)',
					'& + .MuiSwitch-track': {
						backgroundColor: 'var(--euna-brand-primary)'
					}
				},
				'& .MuiSwitch-switchBase.Mui-disabled': {
					color: 'default',
					'& + .MuiSwitch-track': {
						backgroundColor: 'default'
					}
				},
				'& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': {
					color: 'color-mix(in srgb, var(--euna-brand-primary) 38%, lightgray)',
					'& + .MuiSwitch-track': {
						backgroundColor: 'var(--euna-brand-primary)'
					}
				}
			}),
			[size]
		);

		return (
			<Switch id={id} disabled={disabled} checked={checked} onChange={handleChange} sx={sxStyles} />
		);
	}
);

ControlledSwitch.displayName = 'ControlledSwitch';

export default ControlledSwitch;
